import { bcDeadlines } from "./bc";
import { Incident, IncidentDocumentation, NotificationParty } from "./types";
import { hasText, asText } from "./text";
import {
  addDays,
  addHours,
  addOntarioBusinessDays,
  formatElapsed,
  isOntarioBusinessHours,
  nextOntarioBusinessMorning
} from "./time";

export type ClockUrgency = "immediate" | "timed" | "required";
export type ClockState = "due_now" | "open" | "overdue" | "done" | "needed" | "advisory";

export interface ComplianceClock {
  id: string;
  label: string;
  detail: string;
  urgency: ClockUrgency;
  state: ClockState;
  /** Always the staff confirmation time. Never the alert time. */
  started_at: string;
  due_at?: string;
  citation: string;
  site_policy?: boolean;
  advisory?: boolean;
}

export interface ComplianceView {
  clocks: ComplianceClock[];
  elapsed_since_alert: string;
  elapsed_since_confirmation?: string;
  route_hint?: string;
  next_business_morning?: string;
  province_note?: string;
  confirmation_note: string;
}

function notified(incident: Incident, parties: NotificationParty[]): boolean {
  return (incident.notifications ?? []).some((row) => parties.includes(row.party) && row.done && Boolean(row.at));
}

function harm(doc: IncidentDocumentation | undefined): { injury: boolean; distress: boolean } {
  const injuries = hasText(doc?.injuries);
  const distressText = asText(doc?.distress).trim().toLowerCase();
  const injury =
    doc?.injuries_none === true
      ? false
      : injuries || doc?.hospital_transfer === "yes" || doc?.returned_injured === "yes";
  const distress = Boolean(hasText(doc?.distress) && distressText !== "none");
  return { injury, distress };
}

function stateFor(
  urgency: ClockUrgency,
  startedAt: string,
  dueAt: string | undefined,
  done: boolean,
  now: Date,
  overdueAt?: string,
  advisory?: boolean
): ClockState {
  if (done) return "done";
  if (advisory) return "advisory";
  if (overdueAt && now.getTime() > new Date(overdueAt).getTime()) return "overdue";
  if (urgency === "required") return "needed";
  if (urgency === "immediate") return "due_now";
  if (!dueAt) return "open";
  return now.getTime() > new Date(dueAt).getTime() ? "overdue" : "open";
}

interface ClockDraft {
  id: string;
  label: string;
  detail: string;
  urgency: ClockUrgency;
  due_at?: string;
  overdue_at?: string;
  done: boolean;
  citation: string;
  site_policy?: boolean;
  advisory?: boolean;
}

function finish(drafts: ClockDraft[], startedAt: string, now: Date): ComplianceClock[] {
  return drafts.map((draft) => ({
    id: draft.id,
    label: draft.label,
    detail: draft.detail,
    urgency: draft.urgency,
    state: stateFor(draft.urgency, startedAt, draft.due_at, draft.done, now, draft.overdue_at, draft.advisory),
    started_at: startedAt,
    due_at: draft.due_at,
    citation: draft.citation,
    site_policy: draft.site_policy,
    advisory: draft.advisory
  }));
}

function missingHours(incident: Incident, now: Date): number | null {
  if (incident.type !== "wandering_zone") return null;
  const doc = incident.documentation ?? {};
  const startIso = doc.missing_last_seen || incident.alert_at;
  const endIso = doc.missing_found_time;
  const end = endIso ? new Date(endIso) : now;
  const start = new Date(startIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  return (end.getTime() - start.getTime()) / 3600000;
}

/**
 * Compliance hints. Every countdown starts at confirmed_at.
 * Immediate duties stay "due now" and never show a grace-period countdown.
 * Returns an empty clock list until a staff member has confirmed the alert.
 */
export function buildCompliance(incident: Incident, now: Date): ComplianceView {
  const elapsed = formatElapsed(new Date(incident.alert_at), now);
  const base: ComplianceView = {
    clocks: [],
    elapsed_since_alert: elapsed,
    confirmation_note:
      "Countdowns start when a staff member confirms the alert. The alert time does not start them. NeuraVue does not submit this record."
  };
  if (!incident.confirmed_at) {
    return base;
  }
  const startedAt = incident.confirmed_at;
  const doc = incident.documentation ?? {};
  const decision = incident.reasonable_grounds_decision?.decision;
  const drafts: ClockDraft[] = [];
  const signals = harm(doc);

  if (incident.province === "ON") {
    const confirmed = new Date(startedAt);
    const business = isOntarioBusinessHours(confirmed);
    base.route_hint = business
      ? "If a report to the Director is required, use the Critical Incident System. Business hours are Monday to Friday, 8:30 a.m. to 4:30 p.m. Eastern, excluding statutory holidays."
      : "If a report to the Director is required, call the after-hours line 1-888-999-6973, then complete the Critical Incident System on the next business day.";
    if (!business) base.next_business_morning = nextOntarioBusinessMorning(confirmed);

    if (decision === "yes") {
      drafts.push({
        id: "on-director",
        label: "Tell the Director now",
        detail: business
          ? "Report through the Critical Incident System. Do not wait."
          : "Call 1-888-999-6973 now, then complete the Critical Incident System on the next business day.",
        urgency: "immediate",
        done: notified(incident, ["ministry_CIS", "ministry_after_hours"]),
        citation: "FLTCA s.28"
      });
      if (signals.injury || signals.distress) {
        drafts.push({
          id: "on-sdm-now",
          label: "Tell the substitute decision-maker now",
          detail: "Injury, pain, or distress was recorded. Notification is immediate.",
          urgency: "immediate",
          done: doc.sdm_responsible === "yes" || notified(incident, ["sdm_family"]),
          citation: "O. Reg. 246/22 s.104(1)(a)"
        });
      } else {
        drafts.push({
          id: "on-sdm-12h",
          label: "Tell the substitute decision-maker",
          detail:
            "No injury, pain, or distress is recorded. The latest time is 12 hours after you confirmed the alert.",
          urgency: "timed",
          due_at: addHours(startedAt, 12),
          done: doc.sdm_responsible === "yes" || notified(incident, ["sdm_family"]),
          citation: "O. Reg. 246/22 s.104(1)(b)"
        });
      }
      drafts.push({
        id: "on-investigation",
        label: "Start the investigation now",
        detail: "Name a lead and a start time. This does not wait.",
        urgency: "immediate",
        done: Boolean(doc.investigation_start),
        citation: "FLTCA s.27(1)"
      });
      drafts.push({
        id: "on-written",
        label: "Written report to the Director",
        detail: "Due 10 days after you confirmed the alert. NeuraVue does not send it.",
        urgency: "timed",
        due_at: addDays(startedAt, 10),
        done: Boolean(doc.official_reference),
        citation: "O. Reg. 246/22 s.112(2)"
      });
    } else if (decision === "unsure") {
      drafts.push({
        id: "on-escalate",
        label: "Ask the nurse in charge now",
        detail: "You marked reasonable grounds as unsure. Escalate. Do not dismiss the alert.",
        urgency: "immediate",
        done: incident.events.some((event) => event.type === "escalated"),
        citation: "FLTCA s.28"
      });
    }

    if (doc.criminal_offence === "yes") {
      drafts.push({
        id: "on-police",
        label: "Call the police now",
        detail: "Record the service, officer, and occurrence number.",
        urgency: "immediate",
        done: notified(incident, ["police"]),
        citation: "O. Reg. 246/22 s.105"
      });
    }

    if (incident.type === "fall") {
      drafts.push({
        id: "on-post-fall",
        label: "Post-fall assessment",
        detail: `Required before you finalize. Tool on file: ${
          incident.post_fall_instrument_default || "the home's falls tool"
        }.`,
        urgency: "required",
        done: Boolean(doc.post_fall_completed_at && doc.post_fall_instrument),
        citation: "O. Reg. 246/22 s.54(2)"
      });
      if (signals.injury) {
        drafts.push({
          id: "on-sdm-prompt",
          label: "Tell the family or substitute decision-maker as soon as you can",
          detail: "Serious injury is documented. There is no grace-period countdown.",
          urgency: "immediate",
          done: notified(incident, ["sdm_family"]),
          citation: "O. Reg. 246/22 s.115(6)"
        });
      }
    }

    if (doc.hospital_transfer === "yes" && doc.significant_change === "yes") {
      drafts.push({
        id: "on-hospital-1bd",
        label: "Tell the Director about the hospital transfer",
        detail:
          "Significant change recorded. Due one business day after you confirmed the alert. Use the Critical Incident System.",
        urgency: "timed",
        due_at: addOntarioBusinessDays(startedAt, 1),
        done: notified(incident, ["ministry_CIS"]),
        citation: "O. Reg. 246/22 s.115(3)4"
      });
    }
    if (doc.hospital_transfer === "yes" && doc.significant_change === "unknown") {
      drafts.push({
        id: "on-hospital-unknown",
        label: "Follow up with the hospital, then the Director if still unknown",
        detail:
          "Contact the hospital within 3 calendar days. If the change is significant or still unknown, tell the Director within 3 business days.",
        urgency: "timed",
        due_at: addDays(startedAt, 3),
        done: notified(incident, ["ministry_CIS"]),
        citation: "O. Reg. 246/22 s.115(4)"
      });
    }

    const away = missingHours(incident, now);
    if (incident.type === "wandering_zone" && away !== null) {
      if (doc.returned_injured === "yes" || away >= 3) {
        drafts.push({
          id: "on-missing-now",
          label: "Tell the Director now about the missing resident",
          detail:
            away >= 3
              ? "The resident was away for 3 hours or more, or is still away. This duty is immediate."
              : "The resident returned injured or with an adverse change. Tell the Director now.",
          urgency: "immediate",
          done: notified(incident, ["ministry_CIS", "ministry_after_hours"]),
          citation: "O. Reg. 246/22 s.115(1)"
        });
      } else if (doc.returned_injured === "no") {
        drafts.push({
          id: "on-missing-1bd",
          label: "Tell the Director the next business day",
          detail:
            "Away under 3 hours and returned with no injury or change. Critical Incident System only. No after-hours phone call.",
          urgency: "timed",
          due_at: addOntarioBusinessDays(startedAt, 1),
          done: notified(incident, ["ministry_CIS"]),
          citation: "O. Reg. 246/22 s.115(3)1"
        });
      }
    }
  }

  if (incident.province === "QC") {
    drafts.push({
      id: "qc-ah223",
      label: "Déclarez l'AH-223-1 dès que possible",
      detail: "Declare the incident or accident on AH-223-1 as soon as you can. NeuraVue does not enter it in SISSS.",
      urgency: "immediate",
      done: notified(incident, ["ah223"]) || Boolean(doc.official_reference),
      citation: "G-1.021 art. 392"
    });
    if (decision === "yes") {
      drafts.push({
        id: "qc-clpqs",
        label: "Signalez au CLPQS sans délai",
        detail: "Report to the local complaints commissioner without delay. A supervisor cannot block this.",
        urgency: "immediate",
        done: notified(incident, ["CLPQS"]),
        citation: "L-6.3 s.21"
      });
    }
    const severity = (doc.qc_severity || "").toUpperCase();
    const disclosure =
      severity.startsWith("D") ||
      severity.startsWith("E") ||
      severity.startsWith("F") ||
      severity.startsWith("G") ||
      severity.startsWith("H") ||
      severity.startsWith("I");
    if (disclosure) {
      drafts.push({
        id: "qc-disclosure",
        label: "Documentez la divulgation dès que possible",
        detail: "Severity D or higher. Record what you told the user or representative, and their reaction.",
        urgency: "immediate",
        done: notified(incident, ["disclosure"]),
        citation: "G-1.021 art. 11"
      });
    }
  }

  if (incident.province === "BC") {
    const template = incident.bc_template;
    base.province_note =
      template === "BC_AL"
        ? "Assisted living. The legal deadline is 24 hours after the event time, including weekends and holidays. The time you confirmed the alert is shown beside it. NeuraVue does not send HLTH 1622."
        : "Long-term care. A duty the regulation calls immediate shows as due now. An overdue mark after the home's grace is site policy. The regulation does not set that number. NeuraVue does not submit the health authority form.";
    bcDeadlines(incident, doc).forEach((row) => {
      const done = notified(incident, [row.party as NotificationParty]);
      if (row.advisory) {
        drafts.push({
          id: `bc-${row.party}`,
          label: row.role,
          detail: row.basis,
          urgency: "required",
          advisory: true,
          done,
          citation: row.party === "bc_aga" ? "Adult Guardianship Act s.46" : "Not a statutory deadline"
        });
        return;
      }
      if (row.legal) {
        drafts.push({
          id: `bc-${row.party}`,
          label: row.role,
          detail: row.basis,
          urgency: "timed",
          due_at: row.dueAt || undefined,
          done,
          citation: "Assisted Living Regulation s.51"
        });
        return;
      }
      drafts.push({
        id: `bc-${row.party}`,
        label: row.role,
        detail: row.basis,
        urgency: "immediate",
        overdue_at: row.dueAt || undefined,
        site_policy: true,
        done,
        citation: template === "BC_AL" ? "Home process" : "Residential Care Regulation"
      });
    });
  }

  if (incident.province === "other") {
    base.province_note =
      "This home has no Ontario or Quebec profile, so provincial countdowns are hidden. Record care, notifications, and your own judgement as usual.";
  }

  base.clocks = finish(drafts, startedAt, now);
  base.elapsed_since_confirmation = formatElapsed(new Date(startedAt), now);
  return base;
}
